import { Page, LoadingModal, OrderTypesProps, OptionProps } from 'components'
import { Filter } from './components/Filter'
import { useDispatch, useSelector } from 'store/store'
import { useCallback, useEffect, useState } from 'react'
import { NDParamsType } from 'commons'
import {
  asyncGetNegotiations,
  resetPagination
} from 'store/NegotiationsContext/negotiationsSlice'
import NDTable from './components/NDTable'
import { Statistics } from './components/Statistics'
import useParams from 'commons/customHooks/useParams'
import { getListaAnoMesCota } from 'services/nd'
import { statusNegDiffApproval } from 'services/nd/constants'
import { getOvTypes } from 'services/manage_ov'
import { getSaleRegions, getSaleTeam } from 'services/salesOrder'
import { SaleRegion } from 'services/salesOrder/types'
import * as S from './styles'
import { ActionTabs, ItemActionTabsType } from 'components/Structures/ActionTabs'
import {
  Channels,
  defaultOfficeOptions,
  defaultOvTypeOptions,
  OVTypes,
  YesNo
} from 'constants/enums'

export const splittingString = (value: string) => {
  return value.length > 0 ? value.split(',') : []
}

type MesCotaType = {
  dt_ano_mes_cota: string
  ds_ano_mes_cota: string
}

export type SortFilterType = {
  column?: string
  isCrescent?: boolean
}

export const NDPage = () => {
  const dispatch = useDispatch()
  const { getParams, createParamsByObject } = useParams()

  const [ovTypes, setOvTypes] = useState<string[]>([] as string[])
  const [optionsMesCota, setOptionsMesCota] = useState<string[]>([] as string[])
  const [optionsOffices, setOptionsOffices] = useState<string[]>([])
  const [optionsOvTypes, setOptionsOvTypes] = useState<string[]>([] as string[])
  const [optionsSalesRegion, setOptionsSalesRegion] = useState<SaleRegion[]>([])
  const [optionsSalesTeam, setOptionsSalesTeam] = useState<OptionProps[]>([])

  const [NDFilterDefaultValues, setNDFilterDefaultValues] = useState({
    codigosEquipeVenda: '',
    escritoriosVenda: optionsOffices.join(','),
    codigosRegiaoVenda: '',
    codigosSapCliente: '',
    razoesSociaisCliente: '',
    numerosOrdemVenda: '',
    numerosOrdemVendaSap: '',
    tiposMaterial: 'Fora de linha,Em linha',
    mesesCota: '',
    tiposOrdemVenda: '',
    material: '',
    causas: 'Prazo,Desconto,Prazo + Desconto,Bonificação',
    statusAprovacao: 'Aprovado,Não aprovado',
    hierarquiaCliente: 'false',
    currentTab: '0'
  })

  useEffect(() => {
    return setOptionsOffices(
      defaultOfficeOptions
        .filter((item) => item.market === Channels.MI)
        .map((item) => item.label)
    )
  }, [])

  const getSearchParams = () => {
    const params = getParams()
    return {
      ...NDFilterDefaultValues,
      ...params,
      mesesCota: params.mesesCota ? params.mesesCota : optionsMesCota.join(','),
      codigosRegiaoVenda: params.codigosRegiaoVenda ? params.codigosRegiaoVenda : '',
      codigosEquipeVenda: params.codigosEquipeVenda ? params.codigosEquipeVenda : ''
    }
  }

  useEffect(() => {
    return () => {
      dispatch(resetPagination())
    }
  }, [dispatch])

  const getSalesOptions = async () => {
    const regionsData = await getSaleRegions()
    if (regionsData) {
      const regionsOptions = regionsData.data.filter((regions) => !!regions.cd_regi_vend)
      setOptionsSalesRegion(regionsOptions)
    }

    const teamData = await getSaleTeam()
    if (teamData) {
      const teamOptions = teamData.data
        .filter((team) => !!team.cd_equi_vend)
        .map((team) => ({
          value: team.cd_equi_vend,
          title: team.ds_equi_vend
        }))
      setOptionsSalesTeam(teamOptions)
    }
  }

  const getAsyncOptions = async () => {
    const quotaOptions = await getListaAnoMesCota()
    const dates = quotaOptions.map((option: MesCotaType) => option.ds_ano_mes_cota)
    setOptionsMesCota(dates)
    setNDFilterDefaultValues({
      ...NDFilterDefaultValues,
      mesesCota: dates.join(',')
    })

    await getOvTypes().then((response) => {
      const ovTypes = response?.data.map((item: OrderTypesProps) => item.ds_tipo_orde)
      const typesToFilter = defaultOvTypeOptions.filter(
        (type) => type.value !== OVTypes.YTRA
      )
      const filteredTypes = ovTypes.filter((ovType: string) =>
        typesToFilter.some((type) => ovType.includes(type.value))
      )

      setOvTypes(filteredTypes)
      setOptionsOvTypes(filteredTypes)
      setNDFilterDefaultValues({
        ...NDFilterDefaultValues,
        mesesCota: dates.join(','),
        tiposOrdemVenda: filteredTypes.join(',')
      })
    })
  }

  useEffect(() => {
    getSalesOptions()
    getAsyncOptions()
  }, [])

  const formatStatus = (status: string) => {
    return splittingString(status).map((item) =>
      item === 'Aprovado'
        ? statusNegDiffApproval.backOffice
        : statusNegDiffApproval.pending
    )
  }

  const formatCausa = (causa: string) => {
    return splittingString(causa).map((item) => {
      if (item === 'Prazo') return 'C'
      else if (item === 'Desconto') return 'D'
      else if (item === 'Bonificação') return 'B'
      else return 'A'
    })
  }

  const formatTiposMaterial = (tiposMaterial: string) => {
    return splittingString(tiposMaterial).map((item) => {
      if (item === 'Fora de linha') return YesNo.YES
      else return YesNo.NO
    })
  }

  const formatTiposOV = (tiposOV: string) => {
    return splittingString(tiposOV).map((item) => item.split(' - ')[0])
  }

  const formatMesCota = (mesCotas: string) => {
    const selectedOptions = splittingString(mesCotas).sort()

    return selectedOptions.map((item) => {
      const year = item.split('/')[1]
      const month = item.split('/')[0]
      return `${year}${month}`
    })
  }

  const formattingNDPayload = (data: NDParamsType) => ({
    ...data,
    codigosEquipeVenda: splittingString(data.codigosEquipeVenda)?.map((item) =>
      item.substring(0, 3)
    ),
    codigosRegiaoVenda: splittingString(data.codigosRegiaoVenda)?.map((item) =>
      item.substring(0, 6)
    ),
    codigosSapCliente: splittingString(data.codigosSapCliente),
    escritoriosVenda: splittingString(data.escritoriosVenda),
    razoesSociaisCliente: splittingString(data.razoesSociaisCliente),
    numerosOrdemVenda: splittingString(data.numerosOrdemVenda),
    numerosOrdemVendaSap: splittingString(data.numerosOrdemVendaSap),
    tiposMaterial: formatTiposMaterial(data.tiposMaterial),
    tiposOrdemVenda: formatTiposOV(data.tiposOrdemVenda),
    mesesCota: formatMesCota(data.mesesCota),
    causas: formatCausa(data.causas),
    statusAprovacao: formatStatus(data.statusAprovacao),
    hierarquiaCliente: data.hierarquiaCliente === 'true'
  })

  const {
    loadingNDs,
    negotiationsStatistics,
    sortFilter,
    pagination: { currentPage }
  } = useSelector((store) => store.negotiations)

  useEffect(() => {
    dispatch(
      asyncGetNegotiations(
        formattingNDPayload(getSearchParams()),
        currentPage,
        sortFilter
      )
    )
  }, [sortFilter, dispatch, currentPage])

  const onFilterSubmit = useCallback(
    (data: NDParamsType) => {
      dispatch(asyncGetNegotiations(formattingNDPayload(data), 1, sortFilter))
    },
    [dispatch, sortFilter]
  )

  const onTabClick = (ovTypesValue: string[], index: string) => {
    setOvTypes(ovTypesValue)
    createParamsByObject({
      ...getSearchParams(),
      currentTab: index,
      tiposOrdemVenda: ovTypesValue.join(',')
    })
    onFilterSubmit({
      ...getSearchParams(),
      tiposOrdemVenda: ovTypesValue.join(',')
    })
  }

  const NDTabsItems: ItemActionTabsType[] = [
    {
      label: 'Todas',
      onClick: (index) => onTabClick(optionsOvTypes, index)
    },
    {
      label: 'NDs',
      onClick: (index) =>
        onTabClick([OVTypes.YPEM, OVTypes.YCPM, OVTypes.YFRQ, OVTypes.YHFQ], index)
    },
    {
      label: 'Bonificações',
      onClick: (index) => onTabClick([OVTypes.YRBA], index)
    }
  ]

  return (
    <Page fullwidth>
      {loadingNDs ? <LoadingModal /> : null}
      <>
        <Filter
          onSubmit={onFilterSubmit}
          getSearchParams={getSearchParams}
          optionsMesCota={optionsMesCota}
          optionsOffices={optionsOffices}
          optionsOvTypes={optionsOvTypes}
          optionsSalesTeam={optionsSalesTeam}
          optionsSalesRegion={optionsSalesRegion}
          defaultValues={NDFilterDefaultValues}
          ovTypes={ovTypes}
        />

        <S.NdCard>
          <ActionTabs items={NDTabsItems} currentItem={getSearchParams().currentTab} />
          {!!Object.keys(negotiationsStatistics).length && <Statistics />}

          <NDTable payload={formattingNDPayload(getSearchParams())} />
        </S.NdCard>
      </>
    </Page>
  )
}

export default NDPage
